import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { TreePine, Mountain, Flame, Gem, Shield } from 'lucide-react';
import { SacredSection, ChristianIcon } from '../components/ChristianElements';

const Materials: React.FC = () => {
  const { t } = useTranslation();

  const materials = [
    {
      icon: TreePine,
      title: t('materials.wood.title'),
      description: t('materials.wood.description'),
      details: t('materials.details.wood', { returnObjects: true }) as string[],
      image: "/images/cross.jpg"
    },
    {
      icon: Mountain,
      title: t('materials.soil.title'),
      description: t('materials.soil.description'),
      details: t('materials.details.soil', { returnObjects: true }) as string[],
      image: "/images/soil.webp"
    },
    {
      icon: Flame,
      title: t('materials.incense.title'),
      description: t('materials.incense.description'),
      details: t('materials.details.incense', { returnObjects: true }) as string[],
      image: "/images/Incense.jpg"
    },
    {
      icon: TreePine,
      title: t('materials.oliveBranch.title'),
      description: t('materials.oliveBranch.description'),
      details: t('materials.details.oliveBranch', { returnObjects: true }) as string[],
      image: "/images/treebranch.png"
    },
    {
      icon: Gem,
      title: t('materials.rock.title'),
      description: t('materials.rock.description'),
      details: t('materials.details.rock', { returnObjects: true }) as string[],
      image: "/images/jerusalemrock.jpg"
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('meta.materials.title')}</title>
        <meta 
          name="description" 
          content={t('meta.materials.description')} 
        />
        <link rel="canonical" href={t('meta.materials.canonical')} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 md:pt-24 pb-12 md:pb-16 bg-primary-50 holy-land-bg">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center px-4">
            <div className="mb-4 md:mb-6">
              <ChristianIcon icon="olive" size="lg" className="mx-auto text-gold-600" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-scripture font-bold text-primary-800 mb-4 md:mb-6">
              {t('materials.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 px-2">
              {t('materials.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Materials Overview */}
      <SacredSection className="section-padding bg-gradient-to-b from-white to-primary-50" title={t('materials.sections.sacredMaterials')}>
        <div className="container-max">
          <div className="space-y-16 md:space-y-20 px-4">
            {materials.map((material, index) => (
              <div key={index} className="group grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center sacred-border p-8 md:p-12 bg-white/80 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-500 rounded-2xl border border-primary-100">
                <div className="space-y-6">
                  <div className="relative">
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <material.icon className="w-10 h-10 md:w-12 md:h-12 text-primary-700" />
                    </div>
                    <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 animate-pulse">
                      <ChristianIcon icon="cross" size="sm" className="text-gold-600" />
                    </div>
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-scripture font-bold text-primary-800 mb-4 group-hover:text-primary-900 transition-colors duration-300">
                      {material.title}
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                      {material.description}
                    </p>
                  </div>
                  
                  <ul className="space-y-4">
                    {material.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start space-x-4 rtl:space-x-reverse group/item hover:bg-primary-50/50 p-3 rounded-lg transition-colors duration-200">
                        <Shield className="w-5 h-5 md:w-6 md:h-6 text-primary-600 mt-0.5 flex-shrink-0 group-hover/item:text-primary-700 transition-colors" />
                        <span className="text-base md:text-lg text-gray-700 leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="relative">
                  <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl overflow-hidden shadow-2xl group-hover:shadow-3xl transition-shadow duration-500">
                    <img 
                      src={material.image}
                      alt={material.title}
                      className={`w-full h-auto object-contain transition-transform duration-500 group-hover:scale-105 ${material.image.includes('cross') ? 'transform rotate-180' : ''}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-gold-500 rounded-full flex items-center justify-center shadow-lg">
                    <ChristianIcon icon="cross" size="sm" className="text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Scripture quote */}
          {/* <div className="mt-8 md:mt-12">
            <ScriptureQuote 
              verse={t('scriptures.peace.verse')}
              reference={t('scriptures.peace.reference')}
              className="max-w-2xl mx-auto"
            />
          </div> */}
        </div>
      </SacredSection>

      {/* Sacred Soil Process *
      <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-2 md:mb-4">
              {t('materials.sacredSoilProcess.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-4">
              {t('materials.sacredSoilProcess.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {(t('materials.sacredSoilProcess.steps', { returnObjects: true }) as Array<{title: string, description: string}>).map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                  <span className="text-white font-bold text-sm md:text-xl">{index + 1}</span>
                </div>
                <h3 className="text-sm md:text-lg font-semibold text-primary-800 mb-2">{step.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 px-2">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
/}
      {/* Sustainability 
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-bold text-primary-800 mb-4 md:mb-6">
                {t('materials.sustainability.title')}
              </h2>
              <div className="space-y-3 md:space-y-4 text-sm md:text-base text-gray-600">
                {(t('materials.sustainability.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
                  <p key={index}>
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      */}
      
    </>
  );
};

export default Materials;
